import * as yup from 'yup';
import { parse, isDate, format } from 'date-fns';
import { validateCC, validateAge } from '../../modules/validators';
import { isValidCard, cards, detectCardType } from '../../modules/cardUtils';

const zip = /^[0-9]{5}(?:-[0-9]{4})?$/;
const phone = /\(\d{3}\)\s\d{3}-\d{4}/;
const email = /^(?!\.)(?!.*\.{2})([a-zA-Z0-9._+-]+(?<!\.))@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function parseDateString(value, originalValue) {
  const parsedDate = isDate(originalValue)
    ? originalValue
    : parse(originalValue, 'MM/dd/yyyy', new Date());

  return parsedDate;
}

const getSchema = ({ cardsAccepted, formConfig, insuranceName, providerId }) => yup.object().shape({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().matches(email, 'Invalid email address').required('Email is required'),
  phone: yup.string().matches(phone, { message: 'Invalid phone number' }).required('Phone number is required'),
  moveInDate: yup
    .date()
    .transform(parseDateString)
    .typeError('Should be a valid Date')
    .required('Move-in date is required'),

  insuranceRequired: yup.boolean(),
  insurance: yup.string().when('insuranceRequired', {
    is: true,
    then: (schema) => schema.required(`${insuranceName} cannot be blank`),
  }),
  showLock: yup.boolean(),
  lock: yup.string().when('showLock', {
    is: true,
    then: (schema) => schema.required('Lock selection cannot be blank'),
  }),
  driversLicense: yup.object().shape({
    number: yup.string().required('Drivers license number is required'),
    state: yup.string().required('State is required'),
    expirationDate: yup
      .date()
      .transform(parseDateString)
      .typeError('Should be a valid Date')
      .required('Expiration date is required')
      .test('is-valid-exp', 'Expiration date must be after today', (value) => new Date(value) > new Date()),
    birthDate: yup
      .date()
      .transform(parseDateString)
      .typeError('Should be a valid Date')
      .required('Birth date is required')
      .test('is-valid-format', 'Should be a valid birth date', (value) => {
        if (!(value instanceof Date) || Number.isNaN(value)) { return false; }
        const year = value.getFullYear();
        if (year.toString().length !== 4) { return false; }
        const formattedDate = format(value, 'MM/dd/yyyy');
        return /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/.test(formattedDate);
      })
      .test('is-after-1900', 'Birth date must be after the year 1900', (value) => {
        if (!(value instanceof Date) || Number.isNaN(value)) { return false; }
        return value.getFullYear() >= 1900;
      })
      .test('is-valid-age', 'Primary tenant must be at least 18 years old', (value) => validateAge(value)),
  }),
  dlImage: yup.mixed().test(
    'fileSize',
    ({ value }) => {
      const size = (value?.[0]?.size ?? 0) / 1000000;
      const maxSize = providerId === 'se' ? 10 : 100;
      return `The file you attempted to upload is ${size.toFixed(2)} MB. Files must be less than ${maxSize}MB.`;
    },
    (value) => {
      if (!value?.length) { return true; }
      const maxSize = providerId === 'se' ? 10_000_000 : 100_000_000;
      return value[0].size <= maxSize;
    },
  ),
  billing: yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    addressOne: yup.string().required('Address is required'),
    addressTwo: yup.string(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zipCode: yup.string().matches(zip, { message: 'Zip code is invalid' }).required('Zip code is required'),
  }),
  mailing: yup.object().shape({
    addressOne: yup.string().required('Address is required'),
    addressTwo: yup.string(),
    city: yup.string().required('City is required'),
    state: yup.string().required('State is required'),
    zipCode: yup.string().matches(zip, { message: 'Zip code is invalid' }).required('Zip code is required'),
  }),
  hasVehicle: yup.string().required(),
  militaryStatus: yup.string().required(),
  vehicles: yup
    .array()
    .of(
      yup.object({
        description: yup.string().when('hasVehicle', (val, schema) => (val ? schema.required('Description is required') : schema)),
        make: yup.string().when('hasVehicle', (val, schema) => (val ? schema.required('Make is required') : schema)),
        model: yup.string().when('hasVehicle', (val, schema) => (val ? schema.required('Model is required') : schema)),
        VIN: yup.string().when('hasVehicle', (val, schema) => (val ? schema.required('VIN is required') : schema)),
        state: yup.string().when('hasVehicle', (val, schema) => (val ? schema.required('State is required') : schema)),
        license: yup.string().when('hasVehicle', (val, schema) => (val ? schema.required('License plate is required') : schema)),
      }),
    )
    .max(formConfig.vehicleCount),
  contacts: yup
    .array()
    .of(
      yup.object({
        firstName: yup.string(),
        lastName: yup.string(),
        relationship: yup.string(),
        phone: yup.string().matches(phone, { message: 'Invalid phone number', excludeEmptyString: true }),
      }),
    )
    .max(formConfig.contactCount),
  lienholders: yup
    .array()
    .of(
      yup.object({
        name: yup.string(),
        address1: yup.string(),
        address2: yup.string(),
        city: yup.string(),
        state: yup.string(),
        zipCode: yup.string().matches(zip, { message: 'Zip code is invalid', excludeEmptyString: true }),
      }),
    )
    .max(formConfig.lienholderCount),
  password: yup.string().required('Password is required').min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('password'), null], 'Passwords must match'),

  securityQuestionsRequired: yup.boolean(),
  securityQuestionOne: yup.string().when('securityQuestionsRequired', {
    is: true,
    then: (schema) => schema.required('Security Question 1 is required'),
  }),
  securityAnswerOne: yup.string().when('securityQuestionsRequired', {
    is: true,
    then: (schema) => schema.required('Security Answer 1 is required'),
  }),
  securityQuestionTwo: yup.string().when('securityQuestionsRequired', {
    is: true,
    then: (schema) => schema.required('Security Question 2 is required'),
  }),
  securityAnswerTwo: yup.string().when('securityQuestionsRequired', {
    is: true,
    then: (schema) => schema.required('Security Answer 2 is required'),
  }),

  ccNumber: yup.string()
    .required('Credit card number is required')
    .test('cc-number', 'Card number is invalid', (value) => validateCC(value, cardsAccepted)),

  ccExp: yup.string()
    .required('Expiration date is required')
    .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Invalid expiration date')
    .test('is-valid-exp', 'Expiration date must be after today', (value) => {
      const [month, year] = value.split('/');
      return new Date(`${month}/31/20${year}`) > new Date();
    }),

  ccCSC: yup
    .string()
    .required('CVV is required')
    .matches(/^\d+$/, 'Invalid CVV format')
    .test('valid-cvv-length', 'Invalid CVV length for this card', function cvvLengthValidation(value) {
      const { ccNumber } = this.parent;
      const cardType = detectCardType(ccNumber || '');
      const card = cards.find((c) => c.type === cardType);

      if (!card) { return false; }
      return card.cvcLength.includes(value?.length || 0);
    }),
  dcNumber: yup.string().required('Debit card number is required')
    .test('dc-number', 'Card number is invalid', (value) => isValidCard(value)),

  dcExp: yup.string()
    .required('Debit card expiration date is required')
    .matches(/^(0[1-9]|1[0-2])\/\d{2}$/, 'Invalid expiration date')
    .test('is-valid-exp', 'Expiration date must be after today', (value) => {
      const [month, year] = value.split('/');
      return new Date(`${month}/31/20${year}`) > new Date();
    }),

  dcCSC: yup
    .string()
    .required('Debit card CSC is required')
    .matches(/^\d+$/, 'Invalid CVV')
    .test('valid-dc-cvv-length', 'Invalid CVV length for this card', function cvvLengthValidationFunc(value) {
      const { dcNumber } = this.parent;
      const cardType = detectCardType(dcNumber || '');
      const card = cards.find((c) => c.type === cardType);

      if (!card) { return false; }

      return card.cvcLength.includes(value?.length || 0);
    }),

  achFirstName: yup.string().required('First name is required'),
  achLastName: yup.string().required('Last name is required'),
  achBankName: yup.string().required('Bank name is required'),
  achAccountType: yup.string()
    .required('Account type is required'),
  achAccountNumber: yup.string()
    .required('Account Number is required')
    .matches(/^\d+$/, 'Account Number must be numeric')
    .min(4, 'Account Number must be at least 4 digits')
    .max(17, 'Account Number must be at most 17 digits'),
  achRoutingNumber: yup.string()
    .required('Routing Number is required')
    .matches(/^\d{9}$/, 'Routing Number must be 9 digits'),
});

export default getSchema;
